import {
  Directive,
  ElementRef,
  Input,
  WritableSignal,
  effect,
  inject,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, fromEvent, map, pipe, switchMap, takeUntil, tap } from 'rxjs';

type CellIndices = { row: number; column: number };

@Directive({
  selector: '[rangeSelection]',
})
export class RangeSelectionDirective {
  @Input() selectionClass = 'state--selected';
  @Input() readonly dragging: WritableSignal<boolean> = signal(false);

  readonly selected = output<CellIndices[]>();
  readonly selectionReset = output<void>();

  selectedRange = new Set<HTMLTableCellElement>();

  private readonly tableRef = inject(ElementRef<HTMLTableElement>);
  private readonly table = this.tableRef.nativeElement as HTMLTableElement;
  private startCell: HTMLTableCellElement | null = null;

  private cellIndices = new Map<
    HTMLTableCellElement,
    { row: number; column: number }
  >();

  private readonly selecting = signal(false);

  constructor() {
    this.initListeners();

    effect(() => {
      if (this.dragging()) {
        this.selecting.set(false);
        this.clearCells();
      }
    });
  }

  private initListeners(): void {
    const withCell = pipe(
      map((event: MouseEvent) => ({
        event,
        cell:
          (event.target as HTMLElement).closest<HTMLTableCellElement>(
            'th,td',
          ) ?? null,
      })),
      filter(
        ({ cell }) => !!cell && !cell.classList.contains('non-selectable'),
      ),
    );

    const mouseDown$ = fromEvent<MouseEvent>(this.table, 'mousedown').pipe(
      filter((event) => event.button === 0),
      withCell,
      tap((val) => this.startSelection(val)),
    );

    const mouseOver$ = fromEvent<MouseEvent>(this.table, 'mouseover');

    const mouseUp$ = fromEvent(document, 'mouseup').pipe(
      tap(() => {
        this.selecting.set(true);
      }),
    );

    this.handleOutsideClick();

    mouseDown$
      .pipe(
        switchMap(() => mouseOver$.pipe(takeUntil(mouseUp$))),
        withCell,
        takeUntilDestroyed(),
      )
      .subscribe((val) => this.select(val));
  }

  private handleOutsideClick(): void {
    fromEvent(document, 'mousedown')
      .pipe(takeUntilDestroyed())
      .subscribe((event: MouseEvent) => {
        if (!this.table.checkVisibility()) {
          return;
        }

        const target = event.target as HTMLElement;

        if (!this.table.contains(target as Node)) {
          this.clearCells();
        }

        this.selectionReset.emit();
      });
  }

  private startSelection = ({
    cell,
    event,
  }: {
    event: MouseEvent;
    cell: HTMLTableCellElement | null;
  }): void => {
    if (!cell) {
      return;
    }

    this.updateCellIndices();

    if (!event.ctrlKey && !event.shiftKey) {
      this.clearCells();
    }

    if (event.shiftKey) {
      this.select({ cell });
      return;
    }

    this.selecting.set(true);
    this.startCell = cell;

    const emitValues = [
      this.cellIndices.get(cell),
      this.cellIndices.get(cell),
    ] as CellIndices[];

    this.selected.emit(emitValues);

    if (!event.shiftKey) {
      if (this.selectedRange.has(cell)) {
        this.selectedRange.delete(cell);
      } else {
        this.selectedRange.add(cell);
      }

      cell.classList.toggle(this.selectionClass);
    }
  };

  private select = ({ cell }: { cell: HTMLTableCellElement | null }): void => {
    if (!cell) {
      return;
    }

    this.clearCells();

    if (!this.startCell) {
      return;
    }

    this.getCellsBetween(this.startCell, cell).forEach((item) => {
      this.selectedRange.add(item);
      item.classList.add(this.selectionClass);
    });

    const emitValues = [
      this.cellIndices.get(this.startCell),
      this.cellIndices.get(cell),
    ] as CellIndices[];

    this.selected.emit(emitValues);
  };

  private clearCells(): void {
    Array.from(this.selectedRange).forEach((cell) => {
      cell.classList.remove(this.selectionClass);
    });

    this.selectedRange.clear();
  }

  private getCellsBetween(
    start: HTMLTableCellElement,
    end: HTMLTableCellElement,
  ): HTMLTableCellElement[] {
    const startCoords = this.cellIndices.get(start);
    const endCoords = this.cellIndices.get(end);

    if (!startCoords || !endCoords) {
      return [];
    }

    const boundaries = {
      top: Math.min(startCoords.row, endCoords.row),
      right: Math.max(
        startCoords.column + start.colSpan - 1,
        endCoords.column + end.colSpan - 1,
      ),
      bottom: Math.max(
        startCoords.row + start.rowSpan - 1,
        endCoords.row + end.rowSpan - 1,
      ),
      left: Math.min(startCoords.column, endCoords.column),
    };

    const cells: HTMLTableCellElement[] = [];

    this.iterateCells(this.table, (cell) => {
      const cellIndices = this.cellIndices.get(cell);

      if (!cellIndices) {
        return;
      }

      const { row, column } = cellIndices;

      if (
        column >= boundaries.left &&
        column <= boundaries.right &&
        row >= boundaries.top &&
        row <= boundaries.bottom
      ) {
        cells.push(cell);
      }
    });

    return cells;
  }

  private updateCellIndices(): void {
    this.cellIndices.clear();

    const matrix: number[][] = [];

    this.iterateCells(this.table, (cell, y, x) => {
      for (let spanX = x; spanX < x + cell.colSpan; spanX++) {
        for (let spanY = y; spanY < y + cell.rowSpan; spanY++) {
          matrix[spanY] = matrix[spanY] ?? [];
          matrix[spanY][spanX] = 1;
        }
      }

      this.cellIndices.set(cell, { row: y, column: x });
    });
  }

  private iterateCells(
    table: HTMLTableElement,
    callback: (cell: HTMLTableCellElement, y: number, x: number) => void,
  ): void {
    for (let y = 0; y < table.rows.length; y++) {
      for (let x = 0; x < table.rows[y].cells.length; x++) {
        callback(table.rows[y].cells[x], y, x);
      }
    }
  }
}
